import { useCallback, useEffect, useRef, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  describeManagedFee,
  type BackofficeAssignment,
  type BackofficeAssignmentUpdate,
  type RegistryFeeAdjustmentRequest,
  type ReportAssignments,
} from '@/types/np-assignments';
import { notifyIfBackofficeBehind } from '@/utils/backofficeSyncNotice';

export interface UseBackofficeAssignmentsResult {
  data: ReportAssignments | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  create: (planItemIds: string[]) => Promise<BackofficeAssignment | null>;
  /** Legacy flow: create an assignment from Django-template scope components. */
  createTemplate: (
    templateItems: {
      scope_component_slug: string;
      subject_id: string | null;
      jurisdiction_code: string | null;
    }[]
  ) => Promise<BackofficeAssignment | null>;
  update: (id: string, patch: BackofficeAssignmentUpdate) => Promise<void>;
  duplicate: (id: string) => Promise<BackofficeAssignment | null>;
  /** Send one assignment to Backoffice on a started report (add a partner late). */
  dispatch: (id: string) => Promise<BackofficeAssignment | null>;
  /** Cancel a dispatched assignment (the partner is dropped; the record stays). */
  cancel: (id: string, reason: string) => Promise<BackofficeAssignment | null>;
  /** Admin: set, change or clear the manual adjustment on the fee an
   * Armadillo assignment's registry orders set - the automatic assignment,
   * or a manual one locked to Armadillo. Null when the API refused it (the
   * toast says why). */
  setRegistryFeeAdjustment: (
    id: string,
    request: RegistryFeeAdjustmentRequest
  ) => Promise<BackofficeAssignment | null>;
  addItems: (id: string, planItemIds: string[]) => Promise<void>;
  removeItem: (id: string, planItemId: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  lockNp: (
    id: string,
    networkPartnerId: string,
    opts?: { npFee?: number | null; npDueDate?: string | null; turnaroundTimeDays?: number | null }
  ) => Promise<void>;
  unlockNp: (id: string) => Promise<void>;
  addShortlistNp: (id: string, networkPartnerId: string) => Promise<void>;
  removeShortlistNp: (id: string, npId: string) => Promise<void>;
  /** Pre-lock document preview: share / withdraw silent portal
   * visibility for one shortlisted candidate. */
  sharePreview: (id: string, npId: string) => Promise<void>;
  withdrawPreview: (id: string, npId: string) => Promise<void>;
  attachFile: (id: string, conversationAttachmentId: string) => Promise<void>;
  detachFile: (id: string, conversationAttachmentId: string) => Promise<void>;
}

export interface UseBackofficeAssignmentsOptions {
  /** Called once a mutation that can change which partner is locked on a
   * live assignment has gone through and the list is re-read: a lock,
   * unlock, cancel or delete. The report's registry-documents card
   * follows the lock (orders are offered while Armadillo i-doc is locked),
   * so the page that owns the report or report-request detail re-reads it
   * here, and the card follows without a reload. A failure in that re-read
   * is the page's own, never shown as a failed mutation. */
  onLockChanged?: () => void | Promise<void>;
}

/** What `run` is told about a mutation beyond what to call. */
interface RunOptions {
  /** The mutation can change which partner is locked on a live
   * assignment: `onLockChanged` fires once the list is re-read. */
  changesLock?: boolean;
}

/**
 * The single data source for the assignment-centric NP triage step.
 * Loads every backoffice assignment for a report (with items, files,
 * shortlist, locked NP, readiness) plus the ungrouped HUMAN items, and
 * exposes the full set of mutations. Each mutation refetches so the grid
 * membership chips + assignment cards stay in sync.
 */
export function useBackofficeAssignments(
  reportId: string | null,
  options: UseBackofficeAssignmentsOptions = {}
): UseBackofficeAssignmentsResult {
  const [data, setData] = useState<ReportAssignments | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Read at call time, so a page passing a fresh callback each render
  // neither re-creates the mutators nor gets a stale one.
  const onLockChangedRef = useRef(options.onLockChanged);
  useEffect(() => {
    onLockChangedRef.current = options.onLockChanged;
  });

  const fetchOnce = useCallback(async () => {
    if (!reportId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setData(await vnextApiClient.listBackofficeAssignments(reportId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  // The page's re-read after a lock change. Its failure is logged and
  // swallowed: the mutation itself went through.
  const notifyLockChanged = useCallback(async () => {
    try {
      await onLockChangedRef.current?.();
    } catch (err) {
      Sentry.captureException(err);
    }
  }, []);

  // Wraps a mutation: runs it, refetches on success (telling the page when
  // the lock may have changed), and surfaces a notification on failure
  // (returning a fallback value).
  const run = useCallback(
    async <T>(
      fn: () => Promise<T>,
      failTitle: string,
      fallback: T,
      runOptions: RunOptions = {}
    ): Promise<T> => {
      let result = fallback;
      try {
        result = await fn();
        await fetchOnce();
        if (runOptions.changesLock) {
          await notifyLockChanged();
        }
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        notifications.show({ title: failTitle, message: e.message, color: 'red' });
        result = fallback;
      }
      return result;
    },
    [fetchOnce, notifyLockChanged]
  );

  const create = useCallback(
    (planItemIds: string[]) =>
      reportId
        ? run(
            () => vnextApiClient.createBackofficeAssignment(reportId, planItemIds),
            'Could not create assignment',
            null
          )
        : Promise.resolve(null),
    [reportId, run]
  );

  const createTemplate = useCallback(
    (
      templateItems: {
        scope_component_slug: string;
        subject_id: string | null;
        jurisdiction_code: string | null;
      }[]
    ) =>
      reportId
        ? run(
            () => vnextApiClient.createBackofficeTemplateAssignment(reportId, templateItems),
            'Could not create assignment',
            null
          )
        : Promise.resolve(null),
    [reportId, run]
  );

  const update = useCallback(
    async (id: string, patch: BackofficeAssignmentUpdate) => {
      const updated = await run<BackofficeAssignment | null>(
        () => vnextApiClient.updateBackofficeAssignment(id, patch),
        'Could not update assignment',
        null
      );
      notifyIfBackofficeBehind(updated?.backofficeSync);
    },
    [run]
  );

  const duplicate = useCallback(
    (id: string) =>
      run(
        () => vnextApiClient.duplicateBackofficeAssignment(id),
        'Could not duplicate assignment',
        null
      ),
    [run]
  );

  const dispatch = useCallback(
    async (id: string) => {
      const dispatched = await run<BackofficeAssignment | null>(
        () => vnextApiClient.dispatchBackofficeAssignment(id),
        'Could not dispatch the partner',
        null
      );
      if (dispatched) {
        notifications.show({
          title: 'Partner dispatched',
          message: 'The partner request was created and activated in Backoffice.',
          color: 'green',
        });
      }
      return dispatched;
    },
    [run]
  );

  const cancel = useCallback(
    async (id: string, reason: string) => {
      const cancelled = await run<BackofficeAssignment | null>(
        () => vnextApiClient.cancelBackofficeAssignment(id, reason),
        'Could not cancel the partner',
        null,
        { changesLock: true }
      );
      if (cancelled) {
        notifications.show({
          title: 'Partner cancelled',
          message:
            'The partner request was cancelled in Backoffice. The assignment stays on record.',
          color: 'green',
        });
      }
      return cancelled;
    },
    [run]
  );

  const setRegistryFeeAdjustment = useCallback(
    async (id: string, request: RegistryFeeAdjustmentRequest) => {
      const adjusted = await run<BackofficeAssignment | null>(
        () => vnextApiClient.setRegistryFeeAdjustment(id, request),
        'Could not adjust the fee',
        null
      );
      if (adjusted) {
        notifications.show({
          title: request.amount === null ? 'Adjustment removed' : 'Fee adjusted',
          message: `Armadillo's fee on this report is now ${describeManagedFee(adjusted)}.`,
          color: 'green',
        });
        notifyIfBackofficeBehind(adjusted.backofficeSync);
      }
      return adjusted;
    },
    [run]
  );

  const addItems = useCallback(
    async (id: string, planItemIds: string[]) => {
      await run(
        () => vnextApiClient.addBackofficeAssignmentItems(id, planItemIds),
        'Could not add items',
        null
      );
    },
    [run]
  );

  const removeItem = useCallback(
    async (id: string, planItemId: string) => {
      await run(
        () => vnextApiClient.removeBackofficeAssignmentItem(id, planItemId),
        'Could not remove item',
        null
      );
    },
    [run]
  );

  const remove = useCallback(
    async (id: string) => {
      await run(
        () => vnextApiClient.deleteBackofficeAssignment(id),
        'Could not delete assignment',
        undefined,
        { changesLock: true }
      );
    },
    [run]
  );

  const lockNp = useCallback(
    async (
      id: string,
      networkPartnerId: string,
      opts?: {
        npFee?: number | null;
        npDueDate?: string | null;
        turnaroundTimeDays?: number | null;
      }
    ) => {
      await run(
        () => vnextApiClient.lockBackofficeAssignmentNp(id, networkPartnerId, opts),
        'Could not lock in NP',
        null,
        { changesLock: true }
      );
    },
    [run]
  );

  const unlockNp = useCallback(
    async (id: string) => {
      await run(
        () => vnextApiClient.unlockBackofficeAssignmentNp(id),
        'Could not unlock NP',
        null,
        { changesLock: true }
      );
    },
    [run]
  );

  const addShortlistNp = useCallback(
    async (id: string, networkPartnerId: string) => {
      await run(
        () => vnextApiClient.addBackofficeAssignmentShortlistNp(id, networkPartnerId),
        'Could not add NP to shortlist',
        null
      );
    },
    [run]
  );

  const removeShortlistNp = useCallback(
    async (id: string, npId: string) => {
      await run(
        () => vnextApiClient.removeBackofficeAssignmentShortlistNp(id, npId),
        'Could not remove NP from shortlist',
        null
      );
    },
    [run]
  );

  const sharePreview = useCallback(
    async (id: string, npId: string) => {
      await run(
        () => vnextApiClient.sharePreviewShortlistNp(id, npId),
        'Could not share documents with the partner',
        null
      );
    },
    [run]
  );

  const withdrawPreview = useCallback(
    async (id: string, npId: string) => {
      await run(
        () => vnextApiClient.withdrawPreviewShortlistNp(id, npId),
        'Could not withdraw the partner preview',
        null
      );
    },
    [run]
  );

  const attachFile = useCallback(
    async (id: string, conversationAttachmentId: string) => {
      await run(
        () => vnextApiClient.attachBackofficeAssignmentFile(id, conversationAttachmentId),
        'Could not attach file',
        undefined
      );
    },
    [run]
  );

  const detachFile = useCallback(
    async (id: string, conversationAttachmentId: string) => {
      await run(
        () => vnextApiClient.detachBackofficeAssignmentFile(id, conversationAttachmentId),
        'Could not remove file',
        undefined
      );
    },
    [run]
  );

  return {
    data,
    isLoading,
    error,
    refetch: fetchOnce,
    create,
    createTemplate,
    update,
    duplicate,
    dispatch,
    cancel,
    setRegistryFeeAdjustment,
    addItems,
    removeItem,
    remove,
    lockNp,
    unlockNp,
    addShortlistNp,
    removeShortlistNp,
    sharePreview,
    withdrawPreview,
    attachFile,
    detachFile,
  };
}
